// LineTable (job page round): rows of a record in columns: an invoice's lines, the parts on a job,
// what was sold, billed and used side by side, a visit's times. A head row of small muted names (each
// can carry a second line: "Sold / option B"); numbers right-aligned in tabular figures; hairlines
// between rows, a rule under the head and over the foot (a total). A cell can say it's a problem (ink
// words after StateMark's red bar: "missing · see 1") or that there's nothing there (muted: "not sold").
// Wider than its column (a phone), it scrolls sideways inside itself, and then takes the focus so the
// keyboard can scroll it too. Truck stock (BL-203): its rows can come in groups, each with a head (a place's
// stock by category, "Valves 5 · 1 below min"), a row can carry a note under it across the table's width
// (what explains it: "Counted 100 on 19 Oct; −12 on J-24611"), and a line taken off is drawn muted. Red is
// only ever the bar, never the words: red small text is under 4.5:1 off the sheet. A report (the ledger's
// profit and loss, balance sheet, cash flow, BL-189) has no head row, and its rows can be sums: a section's
// sum over a rule in strong figures, and the report's total (gross profit, net profit) larger still.
// Reads only --lines-*.
import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react';
import { StateMark } from '../StateMark/StateMark.tsx';
import './LineTable.css';

export interface LineColumn {
  id: string;
  label: ReactNode;
  /** A second line under its name, lighter: "option B", "INV-18402". */
  sub?: string;
  /** Numbers go at the end. */
  align?: 'start' | 'end';
}
/** A cell: its words, or its words and what they are: problem (or bad, its older name), ink words after
 *  a red bar (StateMark); none, muted. */
export type LineCell = ReactNode | { value: ReactNode; tone: LineTone };
export type LineTone = 'bad' | 'none' | 'problem';
export interface LineRow {
  id: string;
  cells: Record<string, LineCell>;
  /** A line under the row, the table's width, muted: what explains it ("Counted 100 on 19 Oct; −12 on J-24611"). */
  note?: ReactNode;
  /**
   * gone: a line taken off (removed, with an Undo in its cells), drawn muted. sum: a section's sum (Income),
   * over a rule, strong. total: the report's total (Net profit), a sum drawn larger.
   */
  tone?: 'gone' | 'sum' | 'total';
}
/** A group of rows under its own head: a category of a place's stock ("Valves", "5 · 1 below min"). */
export interface LineGroup {
  id: string;
  label: ReactNode;
  /** Muted, beside the head: what's in it ("5 · 1 below min · 1 no min"). */
  says?: ReactNode;
  rows: LineRow[];
}

export interface LineTableProps {
  /** What the table is, for screen readers ("INV-18402’s lines"). */
  label: string;
  columns: LineColumn[];
  rows?: LineRow[];
  /** Rows in groups, each under its head; drawn after `rows`. */
  groups?: LineGroup[];
  /** A last row over a rule: the total. */
  foot?: Record<string, LineCell>;
  /** false: no head row (a report, whose rows say what they are); the columns still name the cells. */
  head?: boolean;
}

const isTone = (c: LineCell): c is { value: ReactNode; tone: LineTone } =>
  typeof c === 'object' && c !== null && 'tone' in c && 'value' in c;

function Cell({ col, cell, head }: { col: LineColumn; cell: LineCell; head?: boolean }) {
  const T = head ? 'th' : 'td';
  return (
    <T
      className="fl-lines__cell"
      data-align={col.align === 'end' ? 'end' : undefined}
      data-tone={isTone(cell) ? cell.tone : undefined}
      scope={head ? 'row' : undefined}
    >
      {isTone(cell) ? (
        cell.tone === 'problem' || cell.tone === 'bad' ? (
          <>
            <StateMark state="problem" />
            {cell.value}
          </>
        ) : (
          cell.value
        )
      ) : (
        cell
      )}
    </T>
  );
}

function Rows({ columns, rows }: { columns: LineColumn[]; rows: LineRow[] }) {
  return rows.map((r) => (
    <Fragment key={r.id}>
      <tr className="fl-lines__row" data-noted={r.note != null ? '' : undefined} data-tone={r.tone}>
        {columns.map((c, i) => (
          <Cell key={c.id} col={c} cell={r.cells[c.id] ?? null} head={i === 0} />
        ))}
      </tr>
      {r.note != null ? (
        <tr className="fl-lines__note-row" data-tone={r.tone}>
          <td className="fl-lines__note" colSpan={columns.length}>
            {r.note}
          </td>
        </tr>
      ) : null}
    </Fragment>
  ));
}

export function LineTable({ label, columns, rows = [], groups = [], foot, head = true }: LineTableProps) {
  const wrap = useRef<HTMLDivElement>(null);
  const [scrolls, setScrolls] = useState(false);
  useEffect(() => {
    const el = wrap.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => setScrolls(el.scrollWidth > el.clientWidth + 1));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div
      ref={wrap}
      className="fl-lines"
      role={scrolls ? 'region' : undefined}
      aria-label={scrolls ? label : undefined}
      tabIndex={scrolls ? 0 : undefined}
    >
      <table className="fl-lines__table" aria-label={label}>
        {head ? (
          <thead>
            <tr>
              {columns.map((c) => (
                <th
                  key={c.id}
                  scope="col"
                  className="fl-lines__head"
                  data-align={c.align === 'end' ? 'end' : undefined}
                >
                  {c.label}
                  {c.sub ? <span className="fl-lines__sub">{c.sub}</span> : null}
                </th>
              ))}
            </tr>
          </thead>
        ) : null}
        {rows.length ? (
          <tbody>
            <Rows columns={columns} rows={rows} />
          </tbody>
        ) : null}
        {groups.map((g) => (
          <tbody key={g.id} className="fl-lines__group">
            <tr>
              <th className="fl-lines__group-head" scope="rowgroup" colSpan={columns.length}>
                {g.label}
                {g.says != null ? <span className="fl-lines__says">{g.says}</span> : null}
              </th>
            </tr>
            <Rows columns={columns} rows={g.rows} />
          </tbody>
        ))}
        {foot ? (
          <tfoot>
            <tr>
              {columns.map((c, i) => (
                <Cell key={c.id} col={c} cell={foot[c.id] ?? null} head={i === 0} />
              ))}
            </tr>
          </tfoot>
        ) : null}
      </table>
    </div>
  );
}
